import { GridColDef } from '@mui/x-data-grid';
import { ReactComponent as FileIcon } from 'assets/download-icon.svg';
import { Typography, Button, Stack } from '@mui/material';
import MuiDataGrid from 'components/MuiDataGrid';
import { IUpedateSkillVideo } from 'apis/survey/type';
import { useLocation } from 'react-router-dom';

interface Props {
  list: IUpedateSkillVideo[];
  isLoading: boolean;
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  onSelected: (value: string[]) => void;
  selected: string[];
}

function AssignmentBody(props: Props) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { list = [], isLoading, totalCount, page, onPageChange } = props;

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleDownloadClick = (url: string, assignmentName: string) => {
    fetch(url, { method: 'GET' })
      .then(res => res.blob())
      .then(blob => {
        const blobURL = URL.createObjectURL(blob);
        const aTag = document.createElement('a');
        aTag.href = blobURL;
        aTag.download = assignmentName || 'download';
        document.body.appendChild(aTag);
        aTag.click();
        document.body.removeChild(aTag);
        URL.revokeObjectURL(blobURL);
      })
      .catch(e => {
        console.error(e);
      });
  };

  const renderAssignment = (
    assignment: any,
    assignmentName: string,
    date: string
  ) => {
    if (!assignment) {
      return <Typography>-</Typography>;
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          sx={{ minWidth: 'auto', padding: '4px' }}
          onClick={() => handleDownloadClick(assignment, assignmentName)}
        >
          <FileIcon />
        </Button>
        <Typography
          sx={{
            fontSize: '13px',
            flexGrow: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {assignmentName}
        </Typography>
        <Typography
          sx={{
            fontSize: '13px',
            whiteSpace: 'nowrap',
            color: 'text.secondary',
          }}
        >
          {formatDate(date)}
        </Typography>
      </Stack>
    );
  };

  const columns: GridColDef[] = [
    { field: 'student_name', headerName: '姓名', width: 160 },
    { field: 'student_no', headerName: '學號／員工編號', width: 160 },
    {
      field: 'student_assignment.assignment1',
      headerName: '作業 1',
      width: 400,
      renderCell: list => {
        if (!list.row.student_assignment) return <Typography>-</Typography>;
        return renderAssignment(
          list.row.student_assignment.assignment1,
          list.row.student_assignment.assignment_name1,
          list.row.student_assignment.assignment_date1
        );
      },
    },
    {
      field: 'student_assignment.assignment2',
      headerName: '作業 2',
      width: 400,
      renderCell: list => {
        if (!list.row.student_assignment) return <Typography>-</Typography>;
        return renderAssignment(
          list.row.student_assignment.assignment2,
          list.row.student_assignment.assignment_name2,
          list.row.student_assignment.assignment_date2
        );
      },
    },
    {
      field: 'student_assignment.assignment3',
      headerName: '作業 3',
      width: 400,
      renderCell: list => {
        if (!list.row.student_assignment) return <Typography>-</Typography>;
        return renderAssignment(
          list.row.student_assignment.assignment3,
          list.row.student_assignment.assignment_name3,
          list.row.student_assignment.assignment_date3
        );
      },
    },
  ];

  const pageSize = params.get('size');

  return (
    <MuiDataGrid
      columns={columns}
      loading={isLoading}
      onPageChange={onPageChange}
      page={page}
      pageSize={Number(pageSize) || 20}
      rows={
        list && Array.isArray(list)
          ? list.map(item => ({ ...item, id: item.student_uuid }))
          : []
      }
      rowCount={totalCount}
      disableFlex={true}
    />
  );
}

export default AssignmentBody;
